/**
 * Currency Utility Functions for Frontend
 * Multi-currency support for USD and IQD
 */

export const CURRENCIES = {
    USD: {
        code: 'USD',
        symbol: '$',
        name: 'US Dollar',
        position: 'before',
        decimals: 2
    },
    IQD: {
        code: 'IQD',
        symbol: 'IQD',
        name: 'Iraqi Dinar',
        position: 'after',
        decimals: 0
    }
};

export const DEFAULT_EXCHANGE_RATES = {
    USD_TO_IQD: 1320,
    IQD_TO_USD: 0.00075758
};

/**
 * Format currency amount with symbol
 * @param {Number} amount - The amount to format
 * @param {String} currency - Currency code (USD or IQD)
 * @returns {String} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
    const currencyInfo = CURRENCIES[currency] || CURRENCIES.USD;
    const numAmount = Number(amount) || 0;
    const formattedAmount = numAmount.toFixed(currencyInfo.decimals);
    const amountWithCommas = formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (currencyInfo.position === 'before') {
        return `${currencyInfo.symbol}${amountWithCommas}`;
    } else {
        return `${amountWithCommas} ${currencyInfo.symbol}`;
    }
};

/**
 * Convert amount from one currency to another
 * @param {Number} amount - Amount to convert
 * @param {String} fromCurrency - Source currency
 * @param {String} toCurrency - Target currency
 * @param {Object} customRates - Custom exchange rates (optional)
 * @returns {Number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, customRates = {}) => {
    if (fromCurrency === toCurrency) {
        return amount;
    }

    const rates = { ...DEFAULT_EXCHANGE_RATES, ...customRates };
    const conversionKey = `${fromCurrency}_TO_${toCurrency}`;

    if (rates[conversionKey]) {
        return amount * rates[conversionKey];
    }

    return amount;
};

/**
 * Format amount in both currencies
 * @param {Number} amount - Primary amount
 * @param {String} primaryCurrency - Primary currency code
 * @param {Object} options - Options including exchange rates
 * @returns {Object} Object with primary and secondary formatted values
 */
export const formatBothCurrencies = (amount, primaryCurrency, options = {}) => {
    const secondaryCurrency = primaryCurrency === 'USD' ? 'IQD' : 'USD';
    const convertedAmount = convertCurrency(amount, primaryCurrency, secondaryCurrency, options.exchangeRates);

    return {
        primary: formatCurrency(amount, primaryCurrency),
        secondary: formatCurrency(convertedAmount, secondaryCurrency),
        primaryAmount: amount,
        secondaryAmount: convertedAmount,
        primaryCurrency,
        secondaryCurrency
    };
};

/**
 * Format currency for display with optional secondary currency
 * @param {Number} amount - Amount to display
 * @param {String} primaryCurrency - Primary currency
 * @param {Boolean} showBoth - Whether to show both currencies
 * @param {Object} options - Additional options
 * @returns {String} Formatted display string
 */
export const formatCurrencyDisplay = (amount, primaryCurrency = 'USD', showBoth = true, options = {}) => {
    const primary = formatCurrency(amount, primaryCurrency);

    if (!showBoth) {
        return primary;
    }

    const both = formatBothCurrencies(amount, primaryCurrency, options);
    return `${both.primary} (${both.secondary})`;
};

/**
 * Get currency symbol
 * @param {String} currency - Currency code
 * @returns {String} Currency symbol
 */
export const getCurrencySymbol = (currency) => {
    return CURRENCIES[currency]?.symbol || '$';
};

/**
 * Parse currency string to number
 * @param {String} currencyString - Formatted currency string
 * @returns {Number} Parsed number
 */
export const parseCurrency = (currencyString) => {
    if (typeof currencyString === 'number') {
        return currencyString;
    }
    const cleaned = String(currencyString).replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
};

/**
 * Get currency select options for dropdowns
 * @returns {Array} Array of currency options
 */
export const getCurrencyOptions = () => {
    return Object.keys(CURRENCIES).map(code => ({
        label: `${CURRENCIES[code].symbol} ${CURRENCIES[code].name}`,
        value: code
    }));
};

/**
 * React hook to use clinic's currency settings
 */
export const useCurrencySettings = () => {
    // This will be populated from clinic settings context
    const defaultSettings = {
        primaryCurrency: 'USD',
        secondaryCurrency: 'IQD',
        displayBothCurrencies: true,
        exchangeRates: DEFAULT_EXCHANGE_RATES
    };

    // In a real implementation, this would get settings from context or API
    return defaultSettings;
};
